import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ContentManagementInterface, {
  ContentItem,
} from '../../../models/ContentManagementInterface';
import FundOpportunity from '../../../models/FundOpportunity';
import Investor from '../../../models/Investor';
import BackendService from '../../../services/admin/BackendService';
import { AUTOSAVE_DELAY_MS } from './newContentManagementEditorConstants';
import {
  appendContentSections,
  assignMissingIds,
  collectContentIds,
  createContentBlock,
  createEmptyContent,
} from './newContentManagementEditorUtils';
import {
  PageMode,
  SaveStatus,
  UseNewContentManagementEditorResult,
} from './newContentManagementEditorTypes';
import { useContentEditorScrollSync } from './useContentEditorScrollSync';
import { restoreCardsAdjust } from './cardsAdjustUtils';
import type { ImageCardsCropMeta } from './useImageCardsSplit';

export function useNewContentManagementEditor(): UseNewContentManagementEditorResult {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [fundOpportunity, setFundOpportunity] = useState<FundOpportunity | null>(null);
  const [content, setContent] = useState<ContentManagementInterface | null>(null);
  const [previewInvestor, setPreviewInvestor] = useState<Investor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [pageMode, setPageMode] = useState<PageMode>('edit');
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  // Tracks changes that have not yet been published. Seeded from the loaded content's `published` flag and
  // flipped back on every edit.
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  // Ids of blocks a recent multi-section paste inserted — drives a brief flash.
  const [recentlyInsertedIds, setRecentlyInsertedIds] = useState<string[]>([]);
  // ImageCarousel blocks that should prime their own "Adjust crops" on
  // mount, keyed by content block id — populated both by a fresh unscoped
  // Documents import and, on load, by restoring any still-live crop source
  // from a previous session (see the content-load effect below).
  const [cardsAdjustByBlockId, setCardsAdjustByBlockId] = useState<
    Record<string, ImageCardsCropMeta>
  >({});
  // Explicit open/closed overrides for a StandardHeaderText block's Footnotes panel, keyed by
  // content block id — absent means "default to open only if it already has footnotes" (see
  // isFootnotesOpen below), so existing footnote content is never hidden on load.
  const [footnotesOpenByBlockId, setFootnotesOpenByBlockId] = useState<Record<string, boolean>>({});

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestContent = useRef<ContentManagementInterface | null>(null);
  const backendService = useRef(new BackendService());
  const toolbarRef = useRef<HTMLDivElement>(null);

  useContentEditorScrollSync({ pageMode, content, toolbarRef });

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    const svc = backendService.current;

    Promise.all([
      svc.getFundOpportunity(id),
      svc.getFundOpportunityContent('FundOpportunity', id, true),
      svc.getFirstInvestorForFund(id),
    ])
      .then(([oppResult, contentJson, firstInvestor]) => {
        setFundOpportunity(oppResult.data);
        setPreviewInvestor(firstInvestor);

        if (contentJson) {
          const parsed: ContentManagementInterface = JSON.parse(contentJson);
          setContent(parsed);
          latestContent.current = parsed;
          setHasUnpublishedChanges(!parsed.published);
          restoreCardsAdjust(svc, 'FundOpportunity', id, parsed, setCardsAdjustByBlockId);
        } else {
          const empty = createEmptyContent(id, oppResult.data.name || '');
          setContent(empty);
          latestContent.current = empty;
          setHasUnpublishedChanges(!empty.published);
        }
      })
      .catch((err) => {
        console.error('Failed to load content:', err);
        setError('Failed to load content. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  const persistContent = useCallback(
    async (updated: ContentManagementInterface) => {
      if (!id) return;
      setSaveStatus('saving');
      try {
        await backendService.current.uploadContentManagementUpdate('FundOpportunity', id, updated);
        setSaveStatus('saved');
      } catch (err) {
        console.error('Save failed:', err);
        setSaveStatus('error');
      }
    },
    [id],
  );

  const scheduleAutoSave = useCallback(
    (updated: ContentManagementInterface) => {
      latestContent.current = updated;
      setSaveStatus('pending');
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        if (latestContent.current) persistContent(latestContent.current);
      }, AUTOSAVE_DELAY_MS);
    },
    [persistContent],
  );

  const updateContent = useCallback(
    (updated: ContentManagementInterface) => {
      setContent(updated);
      // Any edit means the live content now differs from what was published.
      setHasUnpublishedChanges(true);
      scheduleAutoSave(updated);
    },
    [scheduleAutoSave],
  );

  const updateBlock = useCallback(
    (index: number, updatedBlock: ContentItem) => {
      if (!content) return;
      const newBlocks = [...content.content];
      newBlocks[index] = updatedBlock;
      updateContent({ ...content, content: newBlocks });
    },
    [content, updateContent],
  );

  const moveBlock = useCallback(
    (index: number, direction: 'up' | 'down') => {
      if (!content) return;
      const blocks = [...content.content];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= blocks.length) return;
      [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
      updateContent({ ...content, content: blocks });
    },
    [content, updateContent],
  );

  const removeBlock = useCallback(
    (index: number) => {
      if (!content) return;
      const blocks = content.content.filter((_, blockIndex) => blockIndex !== index);
      updateContent({ ...content, content: blocks });
    },
    [content, updateContent],
  );

  const addBlock = useCallback(
    (index: number, type: ContentItem['type'], where: 'before' | 'after') => {
      if (!content || !id) return;
      const newBlock = createContentBlock(type, id);
      const blocks = [...content.content];
      const insertAt = where === 'before' ? index : index + 1;
      blocks.splice(insertAt, 0, newBlock);
      updateContent({ ...content, content: blocks });
    },
    [content, id, updateContent],
  );

  const insertSections = useCallback(
    (index: number, sections: ContentItem[]) => {
      if (!content || sections.length === 0) return;
      // AI-extracted sections carry no ids at any depth (block, heading, item, cell, ...);
      // backfill fresh ones everywhere they're missing or clash with existing/sibling ids
      // so nothing renders with a duplicate/undefined React key downstream.
      const usedIds = collectContentIds(content.content);
      const fresh = sections.map((section) => assignMissingIds(section, usedIds));
      const blocks = [...content.content];
      blocks[index] = fresh[0];
      blocks.splice(index + 1, 0, ...fresh.slice(1));
      updateContent({ ...content, content: blocks });

      setRecentlyInsertedIds(fresh.map((b) => b.id));
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setRecentlyInsertedIds([]), 2200);
    },
    [content, updateContent],
  );

  const appendImportedSections = useCallback(
    (sections: ContentItem[], cropMeta?: ImageCardsCropMeta | null, atIndex?: number) => {
      if (!content || sections.length === 0) return;
      const next = appendContentSections(content, sections, atIndex);
      updateContent(next.content);
      setRecentlyInsertedIds(next.insertedIds);
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setRecentlyInsertedIds([]), 2200);
      // An unscoped (not block-targeted) import that detected cards: prime
      // "Adjust crops" on the one block it created — cropMeta is only ever
      // set for a single-page import, which always produces exactly one
      // ImageCarousel block (see importDocumentSelection's backend contract).
      if (cropMeta?.source && sections.length === 1 && sections[0].type === 'ImageCarousel' && id) {
        const blockId = next.insertedIds[0];
        setCardsAdjustByBlockId((prev) => ({ ...prev, [blockId]: cropMeta }));
        // Best-effort, so this block's Adjust crops survives a reload too.
        backendService.current
          .saveImageCardCropSource({
            modelType: 'FundOpportunity',
            modelId: id,
            contentBlockId: blockId,
            source: cropMeta.source,
            boxes: cropMeta.boxes,
          })
          .catch((err) => console.warn('Could not save the crop source for Adjust crops:', err));
      }
    },
    [content, updateContent, id],
  );

  useEffect(
    () => () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
    },
    [],
  );

  const saveNow = useCallback(async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (latestContent.current) await persistContent(latestContent.current);
  }, [persistContent]);

  const publishNow = useCallback(async () => {
    if (!id) return;
    // Flush any pending autosave first so we publish the latest saved content.
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setPublishing(true);
    try {
      if (latestContent.current) await persistContent(latestContent.current);
      await backendService.current.publishContentManagement('FundOpportunity', id);
      setHasUnpublishedChanges(false);
    } catch (err) {
      console.error('Publish failed:', err);
      setSaveStatus('error');
    } finally {
      setPublishing(false);
    }
  }, [id, persistContent]);

  const revertToPublished = useCallback(async () => {
    if (!id) return;
    // Discard any pending autosave — it's about to be thrown away.
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setReverting(true);
    try {
      await backendService.current.revertContentManagement('FundOpportunity', id);
      const contentJson = await backendService.current.getFundOpportunityContent(
        'FundOpportunity',
        id,
        true,
      );
      if (contentJson) {
        const parsed: ContentManagementInterface = JSON.parse(contentJson);
        setContent(parsed);
        latestContent.current = parsed;
        // The backend strips the `published` flag when it copies published
        // content onto the draft (a revert doesn't count as a formal publish),
        // so `!parsed.published` would read as "still unpublished" here — but
        // the draft now matches published by definition, so there's nothing
        // left to revert or publish.
        setHasUnpublishedChanges(false);
      }
      setSaveStatus('saved');
    } catch (err) {
      console.error('Revert failed:', err);
      setSaveStatus('error');
    } finally {
      setReverting(false);
    }
  }, [id]);

  const goBack = useCallback(() => {
    const from = new URLSearchParams(location.search).get('from');
    navigate(`/opportunities/${from || id}`);
  }, [navigate, id, location.search]);

  return {
    fundOpportunity,
    content,
    previewInvestor,
    loading,
    error,
    saveStatus,
    pageMode,
    setPageMode,
    toolbarRef,
    backendService: backendService.current,
    updateBlock,
    moveBlock,
    removeBlock,
    addBlock,
    insertSections,
    appendImportedSections,
    recentlyInsertedIds,
    cardsAdjustByBlockId,
    clearCardsAdjustForBlock: (blockId: string) =>
      setCardsAdjustByBlockId((prev) => {
        if (!(blockId in prev)) return prev;
        const next = { ...prev };
        delete next[blockId];
        return next;
      }),
    footnotesOpenByBlockId,
    toggleFootnotes: (blockId: string, currentlyOpen: boolean) =>
      setFootnotesOpenByBlockId((prev) => ({ ...prev, [blockId]: !currentlyOpen })),
    saveNow,
    publishNow,
    publishing,
    canPublish: hasUnpublishedChanges,
    revertToPublished,
    reverting,
    goBack,
  };
}
